import { CheckedAddress } from '@sparkdotfi/common-universal'
import { useDisconnect } from 'wagmi'

import { useIPAndAddressCheck } from './useIPAndAddressCheck'
import { TOS_ON_CONNECT_WALLET } from '@/config/chain/flags'
import { useSandbox } from '@/domain/sandbox/useSandbox'
import { useTermsOfService } from '@/domain/state/compliance'
import { useCloseDialog } from '@/domain/state/dialogs/hooks'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'

export type ModalInfo =
  | { type: 'none' }
  | { type: 'terms-of-service'; onAgreeToTermsOfService: () => void }
  | { type: 'vpn-detected' }
  | { type: 'region-blocked'; countryCode: string }
  | { type: 'feature-not-available-in-region'; countryCode: string }
  | { type: 'address-not-allowed'; address: CheckedAddress; disconnect: () => void }
export interface UseComplianceResults {
  visibleModal: ModalInfo
}
export function useCompliance(): UseComplianceResults {
  const { account: address, walletMode } = useConnectedAddress({ throwOnNotConnected: false })
  const { agreedToTermsOfService, saveAgreedToTermsOfService } = useTermsOfService()
  const closeDialog = useCloseDialog()
  const { disconnect } = useDisconnect()
  const { isInSandbox } = useSandbox()
  const isWatchWalletMode = walletMode === 'watch-wallet'

  const ipAndAddressChecks = useIPAndAddressCheck()

  const visibleModal: ModalInfo = (() => {
    if (ipAndAddressChecks.blocked) {
      if (ipAndAddressChecks.reason === 'vpn-detected') {
        return {
          type: 'vpn-detected',
        }
      }

      if (ipAndAddressChecks.reason === 'region-blocked') {
        return {
          type: 'region-blocked',
          countryCode: ipAndAddressChecks.data.countryCode,
        }
      }

      if (address && ipAndAddressChecks.reason === 'address-not-allowed') {
        return {
          type: 'address-not-allowed',
          address,
          disconnect,
        }
      }

      if (ipAndAddressChecks.reason === 'page-not-available-in-region') {
        return {
          type: 'feature-not-available-in-region',
          countryCode: ipAndAddressChecks.data.countryCode,
        }
      }

      return {
        type: 'none',
      }
    }

    if (TOS_ON_CONNECT_WALLET && !!address && !isInSandbox && !isWatchWalletMode && !agreedToTermsOfService(address)) {
      return {
        type: 'terms-of-service',
        onAgreeToTermsOfService: () => saveAgreedToTermsOfService(address),
      }
    }

    return {
      type: 'none',
    }
  })()

  if (visibleModal.type !== 'none') {
    closeDialog() // Close any open dialog
  }

  return {
    visibleModal,
  }
}
